import { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t, Button, SegmentedControl, Checkbox, SearchBox, FilterSelect } from 'Components/ui';
import type { FilterOption } from 'Components/ui';
import type { MembershipTab } from 'Api/axios/course-members-api';

/** Empleado ofrecido en el panel, todavía sin relación con el curso. */
export interface CandidateI {
  id: string;
  fullName: string;
  payrollNum: number | null;
  departmentName: string | null;
  jobTitleName: string | null;
  terminalName: string | null;
}

export interface AddMembersDrawerLabels {
  title: string;
  subtitle: string;
  close: string;
  asMandatory: string;
  asOptional: string;
  searchPlaceholder: string;
  department: string;
  jobTitle: string;
  terminal: string;
  selectPage: string;
  /** Sustantivo que sigue al número: "3 seleccionados". */
  selectedCount: string;
  empty: string;
  cancel: string;
  submit: string;
  loading: string;
}

type FilterKey = 'departmentId' | 'jobTitleId' | 'terminalId';

interface AddMembersDrawerProps {
  open: boolean;
  labels: AddMembersDrawerLabels;
  candidates: CandidateI[];
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  search: string;
  onSearchChange: (value: string) => void;
  departments: FilterOption[];
  jobTitles: FilterOption[];
  terminals: FilterOption[];
  filters: Record<FilterKey, string[]>;
  onFilterChange: (key: FilterKey, value: string[]) => void;
  /** Falso cuando el curso es opcional para toda la empresa. */
  allowOptional: boolean;
  onClose: () => void;
  onSubmit: (userIds: string[], membership: MembershipTab) => Promise<boolean>;
}

const Panel = styled.div({
  width: 560,
  maxWidth: '95vw',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const Head = styled.div({
  flex: 'none',
  padding: '22px 26px',
  borderBottom: `1px solid ${t.color.rowBorder}`,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

const HeadTitle = styled.div({
  fontFamily: t.font,
  fontSize: 18,
  fontWeight: 600,
  color: t.color.title,
});

const HeadSubtitle = styled.div({
  fontFamily: t.font,
  fontSize: 13,
  color: t.color.muted,
  marginTop: 3,
});

const CloseButton = styled.button({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: t.color.muted,
  display: 'inline-flex',
  '& .MuiSvgIcon-root': { fontSize: 24 },
});

const Controls = styled.div({
  flex: 'none',
  padding: '18px 26px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  borderBottom: `1px solid ${t.color.rowBorder}`,
});

const FilterRow = styled.div({
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
});

const SelectAllRow = styled.label({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.text,
  cursor: 'pointer',
});

const List = styled.div({
  flex: 1,
  overflowY: 'auto',
});

const Candidate = styled.label({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '13px 26px',
  borderBottom: `1px solid ${t.color.rowBorder}`,
  cursor: 'pointer',
  '&:hover': { background: t.color.rowHoverBg },
});

const CandidateName = styled.div({
  fontFamily: t.font,
  fontSize: 14,
  fontWeight: 600,
  color: t.color.title,
});

const CandidateMeta = styled.div({
  fontFamily: t.font,
  fontSize: 12.5,
  color: t.color.muted,
});

const Message = styled.div({
  padding: '48px 26px',
  textAlign: 'center',
  fontFamily: t.font,
  fontSize: 14,
  color: t.color.muted,
});

const Footer = styled.div({
  flex: 'none',
  borderTop: `1px solid ${t.color.rowBorder}`,
  padding: '16px 26px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
});

const Pager = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: t.font,
  fontSize: 13,
  color: t.color.muted,
});

const NavButton = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  borderRadius: t.radius.xs,
  border: `1px solid ${t.color.inputBorder}`,
  background: t.color.surface,
  color: t.color.text,
  cursor: 'pointer',
  '& .MuiSvgIcon-root': { fontSize: 18 },
  '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
});

const Selected = styled.span({
  fontFamily: t.font,
  fontSize: 13.5,
  fontWeight: 600,
  color: t.color.title,
});

/**
 * Organismo: panel lateral de alta masiva de miembros.
 *
 * La selección vive en un `Set` local y **no** se reinicia al cambiar de
 * página: el administrador puede recorrer varias páginas armando la tanda. Se
 * limpia solo al cerrar el panel o después de un alta confirmada.
 *
 * La casilla de cabecera selecciona únicamente la página visible, y su etiqueta
 * lo dice. No existe "seleccionar todos los que cumplen el filtro": esa promesa
 * no la puede cumplir un `POST` de ids.
 *
 * Un destino por tanda. Para una mezcla, el administrador hace dos pasadas; es
 * lo que evita mantener el estado de destino fila por fila.
 */
const AddMembersDrawer = ({
  open,
  labels,
  candidates,
  loading,
  page,
  totalPages,
  onPageChange,
  search,
  onSearchChange,
  departments,
  jobTitles,
  terminals,
  filters,
  onFilterChange,
  allowOptional,
  onClose,
  onSubmit,
}: AddMembersDrawerProps) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [membership, setMembership] = useState<MembershipTab>('mandatory');
  const [submitting, setSubmitting] = useState(false);

  // Cerrar el panel descarta la tanda a medio armar.
  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setMembership('mandatory');
    }
  }, [open]);

  // Si el curso deja de admitir opcionales mientras el panel está abierto, el
  // destino no puede quedarse en un valor que el backend va a rechazar.
  useEffect(() => {
    if (!allowOptional) setMembership('mandatory');
  }, [allowOptional]);

  const toggle = (id: string) => {
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageIds = candidates.map(candidate => candidate.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));

  const togglePage = () => {
    setSelected(previous => {
      const next = new Set(previous);
      if (allOnPageSelected) pageIds.forEach(id => next.delete(id));
      else pageIds.forEach(id => next.add(id));
      return next;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    const ok = await onSubmit(Array.from(selected), membership);
    setSubmitting(false);
    if (ok) {
      setSelected(new Set());
      onClose();
    }
  };

  const destinations = allowOptional
    ? [
        { value: 'mandatory' as MembershipTab, label: labels.asMandatory },
        { value: 'optional' as MembershipTab, label: labels.asOptional },
      ]
    : [{ value: 'mandatory' as MembershipTab, label: labels.asMandatory }];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { boxShadow: '-8px 0 30px rgba(16, 24, 40, 0.18)' } }}
    >
      <Panel>
        <Head>
          <div>
            <HeadTitle>{labels.title}</HeadTitle>
            <HeadSubtitle>{labels.subtitle}</HeadSubtitle>
          </div>
          <CloseButton type="button" onClick={onClose} aria-label={labels.close}>
            <Close />
          </CloseButton>
        </Head>

        <Controls>
          <SegmentedControl<MembershipTab> options={destinations} value={membership} onChange={setMembership} />
          <SearchBox value={search} placeholder={labels.searchPlaceholder} onChange={onSearchChange} />
          <FilterRow>
            <FilterSelect
              value={filters.terminalId}
              options={terminals}
              placeholder={labels.terminal}
              onChange={value => onFilterChange('terminalId', value)}
            />
            <FilterSelect
              value={filters.jobTitleId}
              options={jobTitles}
              placeholder={labels.jobTitle}
              onChange={value => onFilterChange('jobTitleId', value)}
            />
            <FilterSelect
              value={filters.departmentId}
              options={departments}
              placeholder={labels.department}
              onChange={value => onFilterChange('departmentId', value)}
            />
          </FilterRow>
          <SelectAllRow>
            <Checkbox
              checked={allOnPageSelected}
              onChange={togglePage}
              aria-label={labels.selectPage}
              disabled={candidates.length === 0}
            />
            {labels.selectPage}
          </SelectAllRow>
        </Controls>

        <List>
          {loading ? <Message>{labels.loading}</Message> : null}
          {!loading && candidates.length === 0 ? <Message>{labels.empty}</Message> : null}
          {!loading &&
            candidates.map(candidate => (
              <Candidate key={candidate.id}>
                <Checkbox
                  checked={selected.has(candidate.id)}
                  onChange={() => toggle(candidate.id)}
                  aria-label={candidate.fullName}
                />
                <div>
                  <CandidateName>{candidate.fullName}</CandidateName>
                  <CandidateMeta>
                    {[candidate.payrollNum ?? '—', candidate.jobTitleName || '—', candidate.departmentName || '—'].join(
                      ' · '
                    )}
                  </CandidateMeta>
                </div>
              </Candidate>
            ))}
        </List>

        <Footer>
          <Pager>
            <NavButton type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              <ChevronLeft />
            </NavButton>
            <span>
              {page} / {Math.max(totalPages, 1)}
            </span>
            <NavButton type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              <ChevronRight />
            </NavButton>
          </Pager>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Selected>
              {selected.size} {labels.selectedCount}
            </Selected>
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {labels.cancel}
            </Button>
            <Button type="button" variant="primary" onClick={submit} disabled={submitting || selected.size === 0}>
              {labels.submit}
            </Button>
          </div>
        </Footer>
      </Panel>
    </Drawer>
  );
};

export default AddMembersDrawer;
